
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FileBarChart2,
  ShieldCheck,
  Users,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  AlertCircle,
} from 'lucide-react';

import { getRolePermissions } from '../../../auth/services/rolePermissions.service';

interface RolePermission {
  permissionName: string;
  permissionActive: boolean;
  permissionDelete: boolean;
  roleActive: boolean;
  roleDelete: boolean;
  rolePermissionActive: boolean;
  rolePermissionDelete: boolean;
}

interface RolePermissionsResponse {
  permissions?: RolePermission[];
}

interface ReportMenuItem {
  permission: string;
  label: string;
  path: string;
  icon: typeof FileBarChart2;
}

const REPORT_ITEMS: ReportMenuItem[] = [
  {
    permission: 'ActivityLogReport',
    label: 'Activity Log Report',
    path: '/public-report/activity-log',
    icon: FileBarChart2,
  },
  {
    permission: 'SummaryReport',
    label: 'Summary Report',
    path: '/public-report/summary-report',
    icon: FileBarChart2,
  },
  {
    permission: 'AccessCreationReport',
    label: 'Access Creation Report',
    path: '/public-report/access-creation',
    icon: Users,
  },
  {
    permission: 'AccessRevocationReport',
    label: 'Access Revocation Report',
    path: '/public-report/access-revocation',
    icon: Users,
  },
  {
    permission: 'AccessModificationReport',
    label: 'Access Modification Report',
    path: '/public-report/access-modification',
    icon: Users,
  },
  {
    permission: 'TrackingReport',
    label: 'Tracking Report',
    path: '/public-report/tracking-report',
    icon: FileBarChart2,
  },
];

/**
 * Supports permission names with or without a role prefix.
 * No role name is hardcoded.
 */
const normalizePermissionName = (name: string): string => {
  const trimmedName = name.trim();
  const lastSeparatorIndex = trimmedName.lastIndexOf('.');

  return (
    lastSeparatorIndex >= 0
      ? trimmedName.substring(lastSeparatorIndex + 1)
      : trimmedName
  )
    .trim()
    .toLowerCase();
};

const ReportSidebar = () => {
  const location = useLocation();

  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [reportsExpanded, setReportsExpanded] = useState(
    location.pathname.startsWith('/public-report'),
  );

  const selectedRoleId = localStorage.getItem('roleId') ?? '';

  useEffect(() => {
    setReportsExpanded(location.pathname.startsWith('/public-report'));
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const loadPermissions = async () => {
      setLoading(true);
      setError('');
      setPermissions(new Set());

      try {
        const employeeCode = localStorage.getItem('employeeCode');
        const roleIdValue = localStorage.getItem('roleId');
        const token = localStorage.getItem('token');

        if (!token || !employeeCode || !roleIdValue) {
          throw new Error(
            'Login and select a role to view available menus.',
          );
        }

        const roleId = Number(roleIdValue);

        if (!Number.isInteger(roleId) || roleId <= 0) {
          throw new Error('The selected role is invalid.');
        }

        const response = (await getRolePermissions(
          employeeCode,
          roleId,
        )) as RolePermissionsResponse;

        const activePermissions = new Set(
          (Array.isArray(response?.permissions)
            ? response.permissions
            : []
          )
            .filter(
              (permission) =>
                permission.permissionActive === true &&
                permission.permissionDelete === false &&
                permission.roleActive === true &&
                permission.roleDelete === false &&
                permission.rolePermissionActive === true &&
                permission.rolePermissionDelete === false,
            )
            .map((permission) =>
              normalizePermissionName(permission.permissionName ?? ''),
            )
            .filter(Boolean),
        );

        if (!cancelled) {
          setPermissions(activePermissions);
        }
      } catch (err) {
        console.error('Failed to load sidebar permissions:', err);

        if (!cancelled) {
          setPermissions(new Set());
          setError(
            err instanceof Error
              ? err.message
              : 'Unable to load menu permissions.',
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [selectedRoleId]);

  const isActive = (path: string) => location.pathname === path;

  const isReportRoute = location.pathname.startsWith('/public-report');

  const linkClass = (active: boolean) =>
    `mx-3 flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition ${
      active
        ? 'bg-teal-50 text-teal-800'
        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
    }`;

  const renderMenu = () => {
    if (loading) {
      return (
        <p className="px-4 py-3 text-sm text-slate-500">
          Loading menu permissions...
        </p>
      );
    }

    if (error) {
      return (
        <div className="mx-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-700">
          <AlertCircle size={17} className="mb-2" />
          {error}
        </div>
      );
    }

    const hasPublicReportPermission = permissions.has(
      normalizePermissionName('PublicReport'),
    );

    const hasNtIdPermission = permissions.has(
      normalizePermissionName('NTIdManagement'),
    );

    const permittedReports = REPORT_ITEMS.filter((item) =>
      permissions.has(normalizePermissionName(item.permission)),
    );

    return (
      <>
        {hasNtIdPermission && (
          <Link
            to="/nt-id-management"
            className={linkClass(isActive('/nt-id-management'))}
          >
            <ShieldCheck size={19} strokeWidth={1.8} />
            <span className="flex-1">NT ID Management</span>
            {isActive('/nt-id-management') && <ChevronRight size={16} />}
          </Link>
        )}

        {hasPublicReportPermission && (
          <div>
            <button
              type="button"
              onClick={() => setReportsExpanded((expanded) => !expanded)}
              aria-expanded={reportsExpanded}
              className={linkClass(isReportRoute)}
            >
              <FileBarChart2 size={19} strokeWidth={1.8} />
              <span className="flex-1 text-left">Public Reports</span>

              {reportsExpanded ? (
                <ChevronDown size={17} />
              ) : (
                <ChevronRight size={17} />
              )}
            </button>

            {reportsExpanded && (
              <div className="mb-2 ml-6 border-l border-slate-200 py-1">
                <Link
                  to="/public-report"
                  className={`mx-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${
                    isActive('/public-report')
                      ? 'bg-teal-50 font-medium text-teal-800'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <span className="flex-1">Available Reports</span>
                </Link>

                {permittedReports.map((report) => {
                  const Icon = report.icon;
                  const active = isActive(report.path);

                  return (
                    <Link
                      key={report.permission}
                      to={report.path}
                      className={`mx-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${
                        active
                          ? 'bg-teal-50 font-medium text-teal-800'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                    >
                      <Icon size={16} strokeWidth={1.8} />
                      <span className="flex-1">{report.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <button
        type="button"
        aria-label={mobileOpen ? 'Close sidebar' : 'Open sidebar'}
        onClick={() => setMobileOpen((open) => !open)}
        className="fixed bottom-5 left-4 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-teal-700 text-white shadow-lg lg:hidden"
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {mobileOpen && (
        <button
          type="button"
          aria-label="Close sidebar overlay"
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-40 bg-slate-950/30 lg:hidden"
        />
      )}

      <aside
        className={`fixed bottom-0 left-0 top-0 z-40 flex w-72 flex-col border-r border-slate-200 bg-white pt-20 shadow-sm transition-transform duration-200 lg:sticky lg:top-0 lg:z-20 lg:h-screen lg:w-64 lg:translate-x-0 lg:pt-6 lg:shadow-none ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="border-b border-slate-100 px-5 pb-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
              <ShieldCheck size={22} />
            </div>

            <div>
              <h2 className="text-sm font-bold text-slate-900">
                WorkVista
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Role-based navigation
              </p>
            </div>
          </div>
        </div>

        <div className="px-5 pb-2 pt-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Navigation
          </p>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto py-2">
          {renderMenu()}
        </nav>

        <div className="border-t border-slate-100 p-4">
          <p className="text-xs leading-5 text-slate-400">
            Menu items are based on your assigned role permissions.
          </p>
        </div>
      </aside>
    </>
  );
};

export default ReportSidebar;
